/**
 * service-reporting-api approvals aggregator client.
 *
 * Wraps the unified cross-service queue at:
 *   GET  /v1/approvals?status=&type=&page=&pageSize=
 *   POST /v1/approvals/:id/approve   { type, reason?, trackingNumber? }
 *   POST /v1/approvals/:id/reject    { type, reason, trackingNumber? }
 *
 * The aggregator pulls from service-mail (address-change), service-finance
 * (settlements), and service-franchise (applications + tier upgrades) and
 * projects every row into a single shape. The downstream service is the
 * actual writer; this client only talks to service-reporting-api.
 *
 * RBAC: `Approval/read`, `Approval/approve`, `Approval/reject` — seeded to
 * platform-admin + dev-superadmin-role by
 * prisma/identity/db-migrations/20260512_approvals_perms.sql.
 */
import { env } from '../env';

import { request } from './client';

export type ApprovalType =
  | 'MAIL_ADDRESS_CHANGE'
  | 'SETTLEMENT'
  | 'FRANCHISE_APPLICATION'
  | 'FRANCHISE_TIER_UPGRADE';

export type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export const APPROVAL_TYPES: readonly ApprovalType[] = [
  'MAIL_ADDRESS_CHANGE',
  'SETTLEMENT',
  'FRANCHISE_APPLICATION',
  'FRANCHISE_TIER_UPGRADE',
] as const;

export const APPROVAL_TYPE_LABEL: Record<ApprovalType, string> = {
  MAIL_ADDRESS_CHANGE: 'Mail – Address change',
  SETTLEMENT: 'Finance – Settlement',
  FRANCHISE_APPLICATION: 'Franchise – Application',
  FRANCHISE_TIER_UPGRADE: 'Franchise – Tier upgrade',
};

export interface ApprovalRowDto {
  readonly id: string;
  readonly type: ApprovalType;
  readonly status: ApprovalStatus;
  readonly submittedAt: string;
  readonly submittedBy: string;
  readonly subjectId: string;
  readonly subjectLabel: string;
  readonly amountMinor?: string;
  readonly metadata: Record<string, unknown>;
}

export interface ListApprovalsResponse {
  readonly items: readonly ApprovalRowDto[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface ListApprovalsParams {
  readonly status?: ApprovalStatus;
  readonly type?: ApprovalType;
  readonly page?: number;
  readonly pageSize?: number;
}

const baseUrl = (): string => env.serviceReportingApiBaseUrl;

export async function listApprovals(
  params: ListApprovalsParams = {},
): Promise<ListApprovalsResponse> {
  return request<ListApprovalsResponse>(baseUrl(), '/v1/approvals', {
    method: 'GET',
    query: {
      status: params.status,
      type: params.type,
      page: params.page,
      pageSize: params.pageSize,
    },
  });
}

export interface ApproveItemInput {
  readonly id: string;
  readonly type: ApprovalType;
  readonly reason?: string;
  /** Required for MAIL_ADDRESS_CHANGE — pulled from row.metadata.trackingNumber. */
  readonly trackingNumber?: string;
}

export async function approveItem(input: ApproveItemInput): Promise<ApprovalRowDto> {
  return request<ApprovalRowDto>(
    baseUrl(),
    `/v1/approvals/${encodeURIComponent(input.id)}/approve`,
    {
      method: 'POST',
      body: {
        type: input.type,
        ...(input.reason !== undefined ? { reason: input.reason } : {}),
        ...(input.trackingNumber !== undefined
          ? { trackingNumber: input.trackingNumber }
          : {}),
      },
    },
  );
}

export interface RejectItemInput {
  readonly id: string;
  readonly type: ApprovalType;
  readonly reason: string;
  readonly trackingNumber?: string;
}

export async function rejectItem(input: RejectItemInput): Promise<ApprovalRowDto> {
  return request<ApprovalRowDto>(
    baseUrl(),
    `/v1/approvals/${encodeURIComponent(input.id)}/reject`,
    {
      method: 'POST',
      body: {
        type: input.type,
        reason: input.reason,
        ...(input.trackingNumber !== undefined
          ? { trackingNumber: input.trackingNumber }
          : {}),
      },
    },
  );
}

/**
 * Helper for MAIL_ADDRESS_CHANGE rows: extract the trackingNumber stashed in
 * `row.metadata` by the aggregator. Returns undefined for non-mail rows.
 */
export function trackingNumberFor(row: ApprovalRowDto): string | undefined {
  if (row.type !== 'MAIL_ADDRESS_CHANGE') return undefined;
  const tn = row.metadata['trackingNumber'];
  return typeof tn === 'string' && tn.length > 0 ? tn : undefined;
}
